import React from 'react';
import { Paper, Stack, Typography } from '@mui/material';
import type { SenseCapHrvMetrics } from '../../types/types';
import { t } from '../../i18n/t';
import { HRV_NORMAL_RANGES, hrvTone, toneColor } from './hrvStatus';

type MetricKey = 'hr' | 'sdnn' | 'rmssd' | 'pnn20' | 'pnn50';

const METRICS: Array<{
    key: MetricKey;
    labelKey: string;
    unitKey: string;
    range: readonly [number, number];
    isHr?: boolean;
}> = [
    { key: 'hr', labelKey: 'sensecap.hr', unitKey: 'sensecap.unitBpm', range: HRV_NORMAL_RANGES.hr, isHr: true },
    { key: 'sdnn', labelKey: 'sensecap.sdnn', unitKey: 'sensecap.unitMs', range: HRV_NORMAL_RANGES.sdnn },
    { key: 'rmssd', labelKey: 'sensecap.rmssd', unitKey: 'sensecap.unitMs', range: HRV_NORMAL_RANGES.rmssd },
    { key: 'pnn20', labelKey: 'sensecap.pnn20', unitKey: 'sensecap.unitPct', range: HRV_NORMAL_RANGES.pnn20 },
    { key: 'pnn50', labelKey: 'sensecap.pnn50', unitKey: 'sensecap.unitPct', range: HRV_NORMAL_RANGES.pnn50 },
];

type HrvMetricsBarProps = {
    hrv: SenseCapHrvMetrics | null;
};

const HrvMetricsBar = ({ hrv }: HrvMetricsBarProps) => {
    if (!hrv) {
        return (
            <Typography color="text.secondary" variant="body2">
                {t('sensecap.hrvEmpty')}
            </Typography>
        );
    }

    return (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
            {METRICS.map((m) => {
                const value = hrv[m.key];
                const tone = hrvTone(value, m.range, !!m.isHr);
                return (
                    <Paper
                        key={m.key}
                        sx={{
                            px: 1.5,
                            py: 1,
                            minWidth: 140,
                            flex: 1,
                            borderLeft: `4px solid ${toneColor(tone)}`,
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {t(m.labelKey)}
                        </Typography>
                        <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                            {value.toFixed(2)}{' '}
                            <Typography component="span" variant="body2" color="text.secondary">
                                {t(m.unitKey)}
                            </Typography>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {t('sensecap.normalRange', {
                                min: String(m.range[0]),
                                max: String(m.range[1]),
                            })}
                        </Typography>
                    </Paper>
                );
            })}
        </Stack>
    );
};

export default HrvMetricsBar;
